import { isRoleModuleEnabled } from './roleModuleAccess.js';

export const isExternalNetworkEnabled = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  const features = user.company_features || user.company_can_access || {};
  return user.network_mode === 'EXTERNAL' && Boolean(features.agent_network);
};

/** Shipment list All / Company / Network tabs — only for EXTERNAL companies with agent network. */
export const showShipmentNetworkScopeTabs = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  return user.network_mode === 'EXTERNAL' && Boolean(features.agent_network);
};

export const hasAttendanceAccess = (user) => {
  if (!user || user.role === 'Driver') return false;
  const companyAccess = user.company_features || user.company_can_access || {};
  const isSystemAdmin = user.role === 'System Admin';
  if (!((isSystemAdmin || Boolean(companyAccess.attendance)) && user.network_mode !== 'EXTERNAL')) {
    return false;
  }
  return isRoleModuleEnabled(user, 'attendance');
};

const attendanceMethodEnabled = (user, methodKey) => {
  if (!hasAttendanceAccess(user)) return false;
  const features = user.company_features || user.company_can_access || {};
  if (Object.prototype.hasOwnProperty.call(features, methodKey)) {
    return Boolean(features[methodKey]);
  }
  return true;
};

/** Header clock-in icon — company Attendance → Manual. Staff clock in by QR only. */
export const canManualAttendance = (user) => {
  const role = user?.role || user?.role_name;
  if (role === 'Staff') return false;
  return attendanceMethodEnabled(user, 'attendance_manual');
};

/** Scan QR modal Attendance tab — company Attendance → QR. */
export const canScanQrAttendance = (user) => attendanceMethodEnabled(user, 'attendance_qr');

/** INTERNAL — Paid Amount Settlement (remit Paid collections). Not limited to cargo. */
export const hasPaidAmountSettlementAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  return true;
};

/** INTERNAL + can_access.commission — ML agent commission (not EXTERNAL XCOM). Cargo optional. */
export const hasMlAgentCommissionAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const features = user.company_features || user.company_can_access || {};
  if (!features.commission) return false;
  const role = user.role_name || user.role;
  if (role === 'Contract Party Admin') return true;
  return ['System Admin', 'Admin', 'Accountant', 'Owner'].includes(role);
};

/** INTERNAL companies with commission enabled — dashboard cards/charts (includes Agent / Hub Admin). */
export const hasInternalCommissionDashboardAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.commission);
};

export const hasOnContractAccess = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  if (user.network_mode === 'EXTERNAL') return Boolean(features.external_on_contract);
  return Boolean(features.on_contract);
};

export const hasOnContractTruckAccess = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  return Boolean(features.on_contract_truck);
};

export const isContractPartyAdmin = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  return role === 'Contract Party Admin' && user.network_mode !== 'EXTERNAL';
};

export const isHubAdmin = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  return role === 'Hub Admin';
};

export const hasContractPartyAdminAccess = isContractPartyAdmin;

export const hasExternalCommissionAccess = (user) => {
  if (!user) return false;
  if (user.network_mode !== 'EXTERNAL') return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.external_commission) || Boolean(features.commission);
};

/** Agent My Partnerships tab — controlled by company can_access.my_partnerships. */
export const hasMyPartnershipsAccess = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  if (role !== 'Agent') return false;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.my_partnerships);
};

/** Shipment crossing — internal company + company setting enabled. */
export const hasShipmentCrossingAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  if (user.is_crossing_enabled === false) return false;
  if (!isRoleModuleEnabled(user, 'crossing')) return false;
  const role = user.role_name || user.role;
  return ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role);
};

/** Internal Agent flagged as pickup man (same role, simplified booking). */
export const isPickupMan = (user) =>
  Boolean(user?.is_pickup_man) &&
  (user.role === 'Agent' || user.role_name === 'Agent') &&
  user.network_mode !== 'EXTERNAL';

export const hasInternalHubDispatchControls = (user) =>
  Boolean(user) && user.network_mode !== 'EXTERNAL';

/** Hub booking menus — false when assigned hub has allow_booking disabled.
 *  Pickup man chooses FROM on the simplified form, so their assigned hub does not block booking.
 */
export const hasLocationHubBookingAccess = (user) => {
  if (!user) return false;
  if (['System Admin', 'Admin', 'Hub Admin'].includes(user.role)) return true;
  if (isPickupMan(user)) return true;
  if (!user.location_hub_id) return true;
  return user.location_hub_allow_booking !== false;
};

const _hubDispatchTypes = (user) => {
  const types = user?.location_hub_dispatch_types;
  if (Array.isArray(types)) return types.map((t) => String(t).toUpperCase());
  if (types) return String(types).split(',').map((t) => t.trim().toUpperCase()).filter(Boolean);
  return [];
};

/** ITCL hub Agent / Hub Admin — can create ITCL (OGCL transfer) requests. */
export const hasOgclTransferCreateAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return false;
  if (user.can_create_ogcl_transfer === true) return true;
  if (user.can_create_ogcl_transfer === false) return false;
  return _hubDispatchTypes(user).includes('ITCL');
};

/** Destination OGCL hub Agent / Hub Admin — incoming ITCL requests after approval. */
export const hasOgclIncomingAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return false;
  return _hubDispatchTypes(user).includes('OGCL');
};

/**
 * Hub Admin at an OGCL destination (and OGCL-only agents) dispatch approved
 * incoming ITCL shipments on an ML sheet — they do not create another ITCL request.
 */
export const shouldDispatchIncomingOgclOnly = (user) => {
  if (!hasOgclIncomingAccess(user)) return false;
  const role = user.role || user.role_name;
  if (role === 'Hub Admin') return true;
  return !hasOgclTransferCreateAccess(user);
};

/** ITCL create is for ITCL-hub Agents only. Hub Admin reviews/unloads, never creates. */
export const canShowOgclTransferCreate = (user) => {
  if (!hasOgclTransferCreateAccess(user)) return false;
  const role = user.role || user.role_name;
  if (role === 'Hub Admin') return false;
  return true;
};

/**
 * First tab on ITCL Requests. Destination OGCL users land on Unload.
 */
export const defaultItclRequestTab = (user) => {
  if (hasOgclIncomingAccess(user) && !canShowOgclTransferCreate(user)) return 'unload';
  return 'pending';
};

/** Keep the tab the user selected. Never snap dest Hub Admin back to Unload. */
export const resolveItclRequestTab = (user, selectedTab) =>
  selectedTab || defaultItclRequestTab(user);

/** Sidebar / list:
 * - ITCL hub Agent/Hub Admin (create + own requests)
 * - Destination OGCL Agent (incoming approved)
 * - Hub Admin / Admin / Accountant / System Admin (approve)
 * Hidden for other roles.
 */
export const hasOgclTransferAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  if (hasOgclTransferCreateAccess(user)) return true;
  if (hasOgclIncomingAccess(user)) return true;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Accountant', 'Hub Admin'].includes(role);
};

/** ITCL Requests sidebar/list — ITCL create users + company admins only.
 * OGCL destination hubs dispatch via ML and should not see this tab.
 */
export const hasOgclTransferRequestMenuAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  if (hasOgclTransferCreateAccess(user)) return true;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Accountant'].includes(role);
};

/**
 * Agent / Hub Admin at a booking-enabled ITCL hub.
 * They raise ITCL (OGCL) requests instead of creating a Dispatch / ML sheet.
 * OGCL destination users still need the Dispatch list for incoming transfers.
 */
export const shouldHideMlSheetDispatch = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return false;
  if (!user.location_hub_id) return false;
  if (user.location_hub_allow_booking === false) return false;
  const isItcl =
    hasOgclTransferCreateAccess(user) || _hubDispatchTypes(user).includes('ITCL');
  if (!isItcl) return false;
  if (shouldDispatchIncomingOgclOnly(user)) return false;
  return true;
};

/** Auto ML Creation on Add Shipment — internal company + company setting enabled. */
export const hasAutoMlAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  if (user.is_auto_ml_enabled === false) return false;
  return true;
};

export const canCreateMlSheet = (user) => {
  if (!user) return false;
  if (isPickupMan(user)) return false;
  if (shouldHideMlSheetDispatch(user)) return false;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role);
};

/** ML sheet list page — hidden for ITCL booking-branch users (ITCL Request only). */
export const canAccessMlSheetList = (user) => {
  if (!user) return false;
  return !shouldHideMlSheetDispatch(user);
};

export const mlSheetUnavailableMessage = (user) => {
  if (shouldHideMlSheetDispatch(user)) {
    return 'Dispatch is not used at ITCL booking branches. Use ITCL Requests instead.';
  }
  return 'Pickup man agents cannot create ML sheets. Use today\'s existing sheets to add shipments.';
};

/** QR Create stays for regular agents/admins — pickup man cannot create ML sheets. */
export const canCreateMlSheetByQr = (user) =>
  canCreateMlSheet(user);

/** Add booked shipments onto an existing ML sheet (list row action). */
export const canAddMlSheetShipments = (user) => {
  if (!user) return false;
  if (shouldHideMlSheetDispatch(user)) return false;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role);
};

/** Scan QR — Update Status tab. Hub agents at booking-only branches need allow_delivery; company-wide roles do not. */
export const canScanQrUpdateStatus = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const role = user.role || user.role_name;
  if (!['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role)) return false;
  const companyAccess = user.company_features || user.company_can_access || {};
  const hasShipment = role === 'System Admin' || Boolean(companyAccess.shipment);
  if (!hasShipment || !isRoleModuleEnabled(user, 'update_status')) return false;
  const isCompanyWideRole = ['System Admin', 'Admin', 'Accountant'].includes(role);
  if (isCompanyWideRole) return true;
  return user.location_hub_allow_delivery !== false;
};

/** Default tab when opening the dashboard Scan QR modal. */
export const scanQrModalDefaultMode = (user) => {
  const role = user?.role || user?.role_name;
  if (role === 'Staff') return 'attendance';
  const statusAllowed = canScanQrUpdateStatus(user);
  const attendanceAllowed = canScanQrAttendance(user);
  const crossingAllowed = hasShipmentCrossingAccess(user);
  if (statusAllowed && ['System Admin', 'Admin', 'Accountant'].includes(role)) return 'status';
  if (attendanceAllowed) return 'attendance';
  if (statusAllowed) return 'status';
  if (crossingAllowed) return 'crossing';
  return 'attendance';
};

export const canOpenScanQrModal = (user) =>
  canScanQrAttendance(user) || canScanQrUpdateStatus(user) || hasShipmentCrossingAccess(user);

/** INTERNAL companies must pick at least one ML access agent on create/edit. */
export const isMlAccessUsersRequired = (user) => {
  if (!user) return false;
  return user.network_mode !== 'EXTERNAL';
};

/** Shipment receipt layout — normalize any value to 'short' | 'large' (default 'large'). */
export const getShipmentReceiptSize = (user) =>
  user?.shipment_receipt_size === 'short' ? 'short' : 'large';

export const canAccessDailyPayments = (user) => {
  if (!user) return false;
  return user.role === 'System Admin' || Boolean(user.can_execute_payments);
};

export default isExternalNetworkEnabled;
